import React, { useEffect, useState } from "react";
import "./Supportmessages.css";
import { db, auth } from "../firebase";
import { collection, getDocs, doc, updateDoc, deleteDoc, query, orderBy } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

export default function SupportMessages() {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [userData, setUserData] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, pending, resolved

  const navigate = useNavigate();

  // Check admin permissions
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const userDoc = await getDocs(collection(db, "users"));
          let foundUser = null;
          
          userDoc.forEach((doc) => {
            if (doc.data().uid === user.uid || doc.id === user.uid) {
              foundUser = { id: doc.id, ...doc.data() };
            }
          });

          if (foundUser && foundUser.role === "admin") {
            setUserData(foundUser);
          } else {
            navigate("/admin");
          }
        } catch (error) {
          navigate("/admin");
        }
      } else {
        navigate("/login");
      }
      setUserLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Fetch messages
  useEffect(() => {
    if (!userLoading && userData) {
      fetchMessages();
    }
  }, [userLoading, userData]);

  async function fetchMessages() {
    try {
      setLoading(true);
      const q = query(collection(db, "supportMessages"), orderBy("createdAt", "desc"));
      const snapshot = await getDocs(q);
      
      const msgs = [];
      snapshot.forEach((doc) => {
        msgs.push({ id: doc.id, ...doc.data() });
      });
      
      setMessages(msgs);
    } catch (err) {
      console.error("Error fetching messages:", err);
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusChange(messageId, newStatus) {
    try {
      await updateDoc(doc(db, "supportMessages", messageId), {
        status: newStatus
      });
      
      setMessages(prev => 
        prev.map(msg => 
          msg.id === messageId ? { ...msg, status: newStatus } : msg
        )
      );
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update status");
    }
  }

  async function handleDelete(messageId, userEmail) {
    if (!window.confirm(`Delete message from ${userEmail}? This cannot be undone.`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, "supportMessages", messageId));
      
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
    } catch (err) {
      console.error("Error deleting message:", err);
      alert("Failed to delete message");
    }
  }

  const filteredMessages = messages.filter(msg => {
    if (filter === "all") return true;
    return msg.status === filter;
  });

  const pendingCount = messages.filter(m => m.status === "pending").length;

  if (userLoading || loading) {
    return (
      <div className="support-page">
        <div className="support-loader">
          <div className="spinner"></div>
          <p>Loading support messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="support-page">
      <div className="support-container">
        <header className="support-header">
          <div className="header-top">
            <button 
              className="back-btn"
              onClick={() => navigate("/admin")}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back to Dashboard
            </button>
            <button 
              className="refresh-btn"
              onClick={fetchMessages}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M21.5 2V8M21.5 8H15.5M21.5 8L18.5 5C17.3 3.7 15.7 2.8 13.9 2.4C12.1 2 10.2 2.2 8.5 3C6.8 3.7 5.4 5 4.5 6.5C3.5 8.1 3 10 3 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2.5 22V16M2.5 16H8.5M2.5 16L5.5 19C6.7 20.3 8.3 21.2 10.1 21.6C11.9 22 13.8 21.8 15.5 21C17.2 20.3 18.6 19 19.5 17.5C20.5 15.9 21 14 21 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Refresh
            </button>
          </div>
          <h1>Support Messages</h1>
          <p className="subtitle">
            Messages from users experiencing verification issues
            {pendingCount > 0 && ` • ${pendingCount} pending`}
          </p>
        </header>

        <div className="filter-tabs">
          <button 
            className={`filter-tab ${filter === "all" ? "active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All ({messages.length})
          </button>
          <button 
            className={`filter-tab ${filter === "pending" ? "active" : ""}`}
            onClick={() => setFilter("pending")}
          >
            Pending ({pendingCount})
          </button>
          <button 
            className={`filter-tab ${filter === "resolved" ? "active" : ""}`}
            onClick={() => setFilter("resolved")}
          >
            Resolved ({messages.filter(m => m.status === "resolved").length})
          </button>
        </div>

        {filteredMessages.length === 0 ? (
          <div className="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <h2>No Messages</h2>
            <p>No support messages found with this filter.</p>
          </div>
        ) : (
          <div className="messages-list">
            {filteredMessages.map((msg) => (
              <div key={msg.id} className={`message-card ${msg.status}`}>
                <div className="message-header">
                  <div className="user-info">
                    <span className="user-email">{msg.email}</span>
                    <span className="message-type">{msg.type}</span>
                  </div>
                  <span className={`status-badge ${msg.status}`}>
                    {msg.status}
                  </span>
                </div>
                
                <div className="message-body">
                  <p>{msg.message}</p>
                </div>

                <div className="message-footer">
                  <span className="message-date">
                    {msg.createdAt?.toDate().toLocaleString() || "Just now"}
                  </span>
                  
                  <div className="message-actions">
                    {msg.status === "pending" ? (
                      <button
                        className="btn-resolve"
                        onClick={() => handleStatusChange(msg.id, "resolved")}
                      >
                        Mark Resolved
                      </button>
                    ) : (
                      <button
                        className="btn-reopen"
                        onClick={() => handleStatusChange(msg.id, "pending")}
                      >
                        Reopen
                      </button>
                    )}
                    <button
                      className="btn-delete"
                      onClick={() => handleDelete(msg.id, msg.email)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}